import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Lock, CheckCircle, Loader2, ArrowRight,
  Zap, Brain, DollarSign, Users,
  BarChart3, FolderOpen, Target, Shield, ArrowRightLeft,
  Landmark, ClipboardCheck, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '@/components/SEO';
import FloatingShare from '@/components/FloatingShare';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cxo-placement/artifacts/o33cdnbv_profile_pic.png";

export default function RPaceLoginPage() {
  const navigate = useNavigate();
  const formSectionRef = useRef(null);
  const [activeTab, setActiveTab] = useState('login');

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Request Access state
  const [reqForm, setReqForm] = useState({ full_name: '', profession: 'CA', membership_number: '', firm_name: '', mobile: '', email: '' });
  const [reqLoading, setReqLoading] = useState(false);
  const [reqSubmitted, setReqSubmitted] = useState(false);

  const scrollToForm = (tab) => {
    setActiveTab(tab);
    formSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) { toast.error('Please fill both fields'); return; }
    setLoginLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/partners/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      localStorage.setItem('rpace_token', data.access_token);
      localStorage.setItem('rpace_partner', JSON.stringify(data.partner));
      toast.success(`Welcome, ${data.partner.name}`);
      navigate('/r-pace/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!reqForm.full_name || !reqForm.membership_number || !reqForm.mobile || !reqForm.email) {
      toast.error('Please fill all required fields'); return;
    }
    setReqLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/partners/request-access`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');
      setReqSubmitted(true);
      toast.success('Access request submitted');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReqLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="rpace-login-page">
      <FloatingShare
        path="/r-pace-partner-login"
        title="R-PACE — Readiness Platform for Accelerated Compliance Execution"
        description="Built for CA / CS Professionals to handle CCFS cases end-to-end. Request professional access to the R-PACE workspace by Ratnatrraya Consulting."
      />
      <SEO
        title="R-PACE — Readiness Platform for Accelerated Compliance Execution"
        description="Handle 3-5x more CCFS cases this season. Built for Chartered Accountants and Company Secretaries. Assess in 30 seconds, know eligibility instantly, identify exact filing gaps. By Ratnatrraya Consulting."
        path="/r-pace-partner-login"
        keywords="R-PACE, CA professional portal, CS professional portal, CCFS 2026, company compliance, Ratnatrraya, corporate exit, compliance execution"
      />

      {/* ─── SECTION 1: HEADER (STICKY) ─── */}
      <header className="border-b border-slate-100 bg-white sticky top-0 z-50" data-testid="rpace-header">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md border-2 border-[#1e3a5f] bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img src="/r-logo-navy.png" alt="R" className="h-6 w-6 object-contain" />
            </div>
            <span className="font-bold text-[#1e3a5f] text-lg tracking-tight">R-PACE</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollToForm('login')}
              className="text-xs text-[#1e3a5f] font-medium hover:underline hidden sm:block"
              data-testid="header-login-btn"
            >
              Professional Login
            </button>
            <Button
              size="sm"
              className="bg-[#1e3a5f] hover:bg-[#152d4a] text-xs h-8 px-4"
              onClick={() => scrollToForm('request')}
              data-testid="header-request-btn"
            >
              Request Professional Access
            </Button>
          </div>
        </div>
      </header>

      {/* ─── SECTION 2: HERO (ABOVE THE FOLD) ─── */}
      <section className="py-16 md:py-24 bg-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
            {/* LEFT: Text Block */}
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-[2.1rem] font-bold text-[#1e3a5f] leading-[1.2] tracking-tight mb-6" data-testid="hero-headline">
                Handle 3–5x more CCFS cases this season — without adding a single team member
              </h1>

              <div className="mb-4">
                <p className="text-lg font-semibold text-[#1e3a5f]">R-PACE</p>
                <p className="text-sm text-slate-500">Readiness Platform for Accelerated Compliance Execution</p>
              </div>

              <div className="bg-[#1e3a5f]/[0.04] border-l-3 border-[#1e3a5f] pl-4 py-3 rounded-r-lg mb-5" data-testid="hero-trust-line">
                <p className="text-[15px] text-[#1e3a5f] font-medium italic">
                  You stay the advisor. R-PACE handles the complexity.
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  No need to divert your team during audit/tax season.
                </p>
              </div>

              <p className="text-slate-600 text-sm leading-relaxed mb-8 max-w-lg">
                Built to make companies compliance-ready — from backlog to exit, executed end-to-end.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 mb-3">
                <Button
                  className="bg-[#1e3a5f] hover:bg-[#152d4a] px-6 h-11 gap-2"
                  onClick={() => scrollToForm('request')}
                  data-testid="hero-request-btn"
                >
                  Request Professional Access <ArrowRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 px-6 h-11"
                  onClick={() => scrollToForm('request')}
                  data-testid="hero-try-btn"
                >
                  Try with 1 Case
                </Button>
              </div>
              <p className="text-xs text-slate-400 mb-6">Free assessment. No commitment.</p>

              <p className="text-[11px] text-slate-400">
                Typical case value: <span className="font-medium text-slate-500">₹25,000–₹75,000</span> depending on complexity
              </p>
            </div>

            {/* RIGHT: Flow Diagram */}
            <div className="flex justify-center" data-testid="hero-flow">
              <FlowDiagram />
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROOF STRIP ─── */}
      <section className="py-6 bg-[#1e3a5f]" data-testid="proof-strip">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-white">
            <span className="text-sm font-medium">100+ companies assessed</span>
            <span className="hidden sm:block w-px h-4 bg-white/30" />
            <span className="text-sm font-medium">3 hours vs 3 days</span>
            <span className="hidden sm:block w-px h-4 bg-white/30" />
            <span className="text-sm font-medium">MCA-matched calculations</span>
          </div>
          <p className="text-blue-200/70 text-xs mt-2">
            What takes 3 days manually, R-PACE helps execute in 3 hours — eligibility assessment, exact filing gaps and penalty calculation.
          </p>
        </div>
      </section>

      {/* ─── FOR CA / CS PROFESSIONALS (MERGED: Pain + Benefits + Trust) ─── */}
      <section className="py-16 md:py-20 bg-white" data-testid="for-professionals-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-xl font-bold text-[#1e3a5f] text-center mb-10" data-testid="professionals-heading">
            For CA / CS Professionals
          </h2>

          {/* Pain → Solution hooks (heavy, attention-grabbing) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
              { icon: Target, pain: 'Backlog piling up before CCFS deadline?', solution: 'We handle execution' },
              { icon: DollarSign, pain: 'Losing fee potential on complex cases?', solution: 'Earn more per case' },
              { icon: Zap, pain: 'No time during audit season?', solution: 'No team diversion required' },
            ].map((item, i) => (
              <div key={i} className="bg-white border-l-[3px] border-[#1e3a5f] border-t border-r border-b border-t-slate-200 border-r-slate-200 border-b-slate-200 rounded-xl p-5" data-testid={`professional-point-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-[#1e3a5f]/[0.07] flex items-center justify-center mb-3">
                  <item.icon className="w-4 h-4 text-[#1e3a5f]" />
                </div>
                <p className="text-[13px] text-slate-500 leading-snug mb-2">{item.pain}</p>
                <p className="text-[15px] text-[#1e3a5f] font-bold">{item.solution}</p>
              </div>
            ))}
          </div>

          {/* Benefit items (light, fast-read checklist) */}
          <div className="flex flex-col sm:flex-row gap-x-8 gap-y-3 justify-center mb-6">
            {[
              'Take up complex cases with confidence',
              'Deliver faster outcomes with less effort',
              'Strengthen client trust during CCFS window',
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-2.5" data-testid={`edge-item-${i}`}>
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                <p className="text-sm text-slate-600">{text}</p>
              </div>
            ))}
          </div>

          {/* Capacity highlight */}
          <div className="bg-[#1e3a5f]/[0.03] border border-[#1e3a5f]/10 rounded-xl px-5 py-4 flex items-start gap-3" data-testid="edge-capacity">
            <div className="w-8 h-8 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center shrink-0 mt-0.5">
              <Users className="w-4 h-4 text-[#1e3a5f]" />
            </div>
            <p className="text-sm text-slate-700 font-medium">
              Handle more cases without stretching your team.
            </p>
          </div>

          {/* Trust close */}
          <p className="text-center text-sm text-[#1e3a5f] font-medium mt-8 italic" data-testid="tagline">
            "You stay the advisor. R-PACE handles the complexity."
          </p>
        </div>
      </section>

      {/* ─── SECTION 5: WHY R-PACE ─── */}
      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-100" data-testid="why-rpace-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="text-xl font-bold text-[#1e3a5f] text-center mb-10" data-testid="why-heading">Why R-PACE</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ValueCard icon={Zap} title="Save Time & Reduce Coordination" desc="No more MCA navigation issues, repeated checks, or unclear steps." />
            <ValueCard icon={Brain} title="Focus on Advisory, Not Execution" desc="Focus on advisory and structured execution instead of routine filing effort." />
            <ValueCard icon={DollarSign} title="Earn More per Case" desc="Handle complex cases and unlock additional professional income." />
          </div>
          <p className="text-center text-sm text-[#1e3a5f] font-medium mt-10">
            Save time. Handle more cases. Earn more per case.
          </p>
        </div>
      </section>

      {/* ─── SECTION 6: JOURNEY ─── */}
      <section className="py-16 md:py-20 bg-white" data-testid="journey-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="text-xl font-bold text-[#1e3a5f] text-center mb-4" data-testid="journey-heading">Your Role in the R-PACE Journey</h2>
          <p className="text-sm text-[#1e3a5f] font-medium text-center mb-2 max-w-2xl mx-auto">
            R-PACE prepares every case for execution — whether closure, compliance, or continuation.
          </p>
          <p className="text-sm text-slate-500 text-center mb-12 max-w-xl mx-auto">From case intake to clean exit — here's where you fit in.</p>
          <JourneyTimeline />
        </div>
      </section>

      {/* ─── SECTION 7: ABOUT C-PACE (COMPACT) ─── */}
      <section className="py-12 md:py-16 bg-slate-50 border-y border-slate-100" data-testid="about-cpace">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Landmark className="w-4 h-4 text-[#1e3a5f]" />
            <h2 className="text-base font-bold text-[#1e3a5f]" data-testid="cpace-heading">About C-PACE</h2>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              <span className="font-semibold text-slate-800">C-PACE</span> (MCA) enables faster processing of company closure applications.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">
              R-PACE prepares and executes cases correctly so they can be processed smoothly.
            </p>
          </div>
        </div>
      </section>

      {/* ─── SECTION 9: INSIDE R-PACE (SCREENSHOTS) ─── */}
      <section className="py-16 md:py-20 bg-slate-50 border-t border-slate-100" data-testid="inside-rpace-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="text-xl font-bold text-[#1e3a5f] text-center mb-12" data-testid="tools-heading">Inside R-PACE</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScreenshotCard
              src="/screenshots/calculator.png"
              step="Step 1"
              label="Assess in 30 seconds"
              testId="screenshot-calculator"
            />
            <ScreenshotCard
              src="/screenshots/eligibility.png"
              step="Step 2"
              label="Know eligibility instantly"
              testId="screenshot-eligibility"
            />
            <ScreenshotCard
              src="/screenshots/filing-table.png"
              step="Step 3"
              label="Identify exact filing gaps"
              testId="screenshot-filing"
            />
          </div>
        </div>
      </section>

      {/* ─── SECTION 10: LOGIN / REQUEST ACCESS ─── */}
      <section className="py-16 md:py-20 bg-white" ref={formSectionRef} id="access-section" data-testid="access-section">
        <div className="max-w-lg mx-auto px-6">
          {/* Tab Toggle */}
          <div className="flex gap-1 mb-8 bg-slate-100 rounded-lg p-1 mx-auto w-fit" data-testid="form-tabs">
            <button
              onClick={() => setActiveTab('login')}
              className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === 'login' ? 'bg-white text-[#1e3a5f] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="tab-login"
            >
              Access Workspace
            </button>
            <button
              onClick={() => setActiveTab('request')}
              className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === 'request' ? 'bg-white text-[#1e3a5f] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="tab-request"
            >
              Request Professional Access
            </button>
          </div>

          {/* Login Form */}
          {activeTab === 'login' && (
            <Card className="border border-slate-200 shadow-sm" data-testid="login-card">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <Lock className="w-4 h-4 text-[#1e3a5f]" />
                  <h2 className="text-lg font-semibold text-[#1e3a5f]">Access Your Workspace</h2>
                </div>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="text-xs text-slate-500 uppercase font-medium block mb-1.5">Email or Mobile Number</label>
                    <Input
                      id="login-email"
                      type="text"
                      value={loginEmail}
                      onChange={e => setLoginEmail(e.target.value)}
                      placeholder="you@firm.com"
                      className="h-11"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase font-medium block mb-1.5">Password</label>
                    <Input
                      type="password"
                      value={loginPassword}
                      onChange={e => setLoginPassword(e.target.value)}
                      placeholder="Enter password"
                      className="h-11"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#1e3a5f] hover:bg-[#152d4a] h-11 gap-2" disabled={loginLoading} data-testid="login-submit-btn">
                    {loginLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Enter R-PACE Workspace
                  </Button>
                </form>
                <p className="text-xs text-slate-400 mt-4 text-center">
                  Already onboarded? Use your registered credentials to continue.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Request Access Form */}
          {activeTab === 'request' && (
            <Card className="border border-slate-200 shadow-sm" data-testid="request-access-card">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-[#1e3a5f]" />
                  <h2 className="text-lg font-semibold text-[#1e3a5f]">Join the R-PACE Professional Network</h2>
                </div>
                <p className="text-slate-500 text-xs mb-6">Access is restricted to verified Chartered Accountants and Company Secretaries.</p>

                {reqSubmitted ? (
                  <div className="text-center py-8" data-testid="request-success">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <h3 className="font-semibold text-slate-900 mb-2">Request Submitted</h3>
                    <p className="text-slate-500 text-sm">Your membership will be verified before granting access. The coordination team will contact you once approved.</p>
                  </div>
                ) : (
                  <form onSubmit={handleRequestAccess} className="space-y-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase font-medium block mb-1.5">Full Name *</label>
                      <Input value={reqForm.full_name} onChange={e => setReqForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Your full name" className="h-10" data-testid="req-name" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase font-medium block mb-1.5">Profession *</label>
                      <div className="flex gap-3">
                        {['CA', 'CS'].map(p => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setReqForm(f => ({ ...f, profession: p }))}
                            className={`flex-1 h-10 rounded-lg border text-sm font-medium transition-all ${reqForm.profession === p ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                            data-testid={`req-profession-${p.toLowerCase()}`}
                          >
                            {p === 'CA' ? 'Chartered Accountant' : 'Company Secretary'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase font-medium block mb-1.5">
                        {reqForm.profession === 'CA' ? 'ICAI Membership Number' : 'ICSI Membership Number'} *
                      </label>
                      <Input
                        value={reqForm.membership_number}
                        onChange={e => setReqForm(p => ({ ...p, membership_number: e.target.value }))}
                        placeholder={reqForm.profession === 'CA' ? '6-digit ICAI number' : 'Alphanumeric ICSI number'}
                        className="h-10 font-mono"
                        data-testid="req-membership"
                      />
                      <p className="text-[10px] text-slate-400 mt-1">
                        {reqForm.profession === 'CA' ? 'For CA: ICAI Membership Number (6 digits)' : 'For CS: ICSI Membership Number (Alphanumeric)'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase font-medium block mb-1.5">Firm Name</label>
                      <Input value={reqForm.firm_name} onChange={e => setReqForm(p => ({ ...p, firm_name: e.target.value }))} placeholder="Your firm name" className="h-10" data-testid="req-firm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-500 uppercase font-medium block mb-1.5">Mobile Number *</label>
                        <Input value={reqForm.mobile} onChange={e => setReqForm(p => ({ ...p, mobile: e.target.value }))} placeholder="+91 9876543210" className="h-10" data-testid="req-mobile" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 uppercase font-medium block mb-1.5">Email ID *</label>
                        <Input type="email" value={reqForm.email} onChange={e => setReqForm(p => ({ ...p, email: e.target.value }))} placeholder="you@firm.com" className="h-10" data-testid="req-email" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-[#1e3a5f] hover:bg-[#152d4a] h-11" disabled={reqLoading} data-testid="req-submit-btn">
                      {reqLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Request Professional Access
                    </Button>
                    <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                      Your membership will be verified before granting access.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-[#0f1f33] py-8" data-testid="rpace-footer">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-slate-300 text-sm">
            R-PACE is a structured readiness and execution system designed to help professionals make companies compliance-ready — from filings to final outcomes.
          </p>
          <p className="text-slate-500 text-xs mt-3">
            For assistance, contact the Ratnatrraya coordination team.
          </p>
        </div>
      </footer>
    </div>
  );
}


/* ─── FLOW DIAGRAM (Updated text, same visual structure) ─── */
function FlowDiagram() {
  return (
    <div className="w-full max-w-xl" data-testid="flow-diagram">
      {/* Desktop: horizontal */}
      <div className="hidden md:block">
        <div className="flex items-stretch gap-0">
          {/* LEFT: CA/CS */}
          <div className="flex-1 bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
              <Briefcase className="w-5 h-5 text-blue-700" />
            </div>
            <p className="text-sm font-bold text-blue-800 mb-1">CA / CS</p>
            <p className="text-[10px] text-blue-600 mb-3">Client Case Comes to You</p>
            <div className="space-y-1">
              {['Inactive company', 'Pending filings', 'Client needs clarity'].map((t, i) => (
                <span key={i} className="block text-[9px] bg-blue-100/60 text-blue-700 rounded px-2 py-0.5">{t}</span>
              ))}
            </div>
          </div>

          {/* Arrow 1 */}
          <div className="flex items-center px-1 shrink-0">
            <svg width="24" height="24" viewBox="0 0 24 24" className="text-slate-300">
              <path d="M4 12h14M14 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" fill="none" />
            </svg>
          </div>

          {/* CENTER: R-PACE */}
          <div className="flex-1 bg-[#1e3a5f] rounded-xl p-4 text-center shadow-lg shadow-[#1e3a5f]/20 relative ring-2 ring-[#1e3a5f]/30 ring-offset-2">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-2">
              <ArrowRightLeft className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm font-bold text-white mb-0.5">R-PACE</p>
            <p className="text-[10px] text-blue-200 mb-3">Readiness & Execution Layer</p>
            <div className="grid grid-cols-2 gap-1">
              {[
                { icon: BarChart3, label: '30-sec Assessment' },
                { icon: ClipboardCheck, label: 'Eligibility Check' },
                { icon: FolderOpen, label: 'Filing Gap Detection' },
                { icon: Target, label: 'Execution Support' },
              ].map((feat, i) => (
                <div key={i} className="flex flex-col items-center gap-1 bg-white/10 rounded-lg py-1.5 px-1">
                  <feat.icon className="w-3 h-3 text-blue-200" />
                  <span className="text-[7px] text-blue-100 leading-tight text-center">{feat.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Arrow 2 */}
          <div className="flex items-center px-1 shrink-0">
            <svg width="24" height="24" viewBox="0 0 24 24" className="text-slate-300">
              <path d="M4 12h14M14 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" fill="none" />
            </svg>
          </div>

          {/* RIGHT: C-PACE */}
          <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-2">
              <Landmark className="w-5 h-5 text-slate-600" />
            </div>
            <p className="text-sm font-bold text-slate-700 mb-0.5">C-PACE</p>
            <p className="text-[10px] text-slate-400 mb-3 leading-snug">Ministry of Corporate Affairs,<br />Govt. of India</p>
            <div className="space-y-1">
              <span className="block text-[9px] bg-slate-100 text-slate-600 rounded px-2 py-0.5">MCA Processing</span>
              <div className="flex items-center justify-center gap-1.5 bg-green-50 border border-green-200 rounded-lg py-1.5 px-2">
                <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                <span className="text-[9px] font-semibold text-green-700">Strike-off Order Issued</span>
              </div>
            </div>
          </div>
        </div>

        {/* Connecting flow label */}
        <div className="flex items-center justify-center gap-2 mt-4 text-[10px] text-slate-400">
          <span>CA / CS</span>
          <ArrowRight className="w-3 h-3" />
          <span className="font-semibold text-[#1e3a5f]">R-PACE</span>
          <ArrowRight className="w-3 h-3" />
          <span>C-PACE</span>
          <ArrowRight className="w-3 h-3" />
          <span className="flex items-center gap-0.5"><CheckCircle className="w-3 h-3 text-green-500" /> Clean Exit</span>
        </div>
      </div>

      {/* Mobile: vertical stack */}
      <div className="md:hidden space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <Briefcase className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <p className="text-sm font-bold text-blue-800">CA / CS</p>
            <p className="text-[10px] text-blue-600">Inactive company, pending filings, client needs clarity</p>
          </div>
        </div>
        <div className="flex justify-center"><ArrowRight className="w-4 h-4 text-slate-300 rotate-90" /></div>
        <div className="bg-[#1e3a5f] rounded-xl p-4 text-center shadow-lg shadow-[#1e3a5f]/20">
          <p className="text-sm font-bold text-white mb-0.5">R-PACE</p>
          <p className="text-[10px] text-blue-200 mb-2">Readiness & Execution Layer</p>
          <div className="flex flex-wrap gap-1.5 justify-center">
            {['30-sec Assessment', 'Eligibility Check', 'Filing Gap Detection', 'Execution Support'].map((l, i) => (
              <span key={i} className="text-[8px] text-blue-100 bg-white/10 rounded px-2 py-1">{l}</span>
            ))}
          </div>
        </div>
        <div className="flex justify-center"><ArrowRight className="w-4 h-4 text-slate-300 rotate-90" /></div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
            <Landmark className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-700">C-PACE</p>
            <p className="text-[10px] text-slate-400">MCA Processing</p>
          </div>
        </div>
        <div className="flex justify-center"><ArrowRight className="w-4 h-4 text-slate-300 rotate-90" /></div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span className="text-sm font-semibold text-green-700">Strike-off Order Issued</span>
        </div>
      </div>

      {/* Power line */}
      <div className="text-center mt-5 space-y-0.5" data-testid="flow-power-line">
        <p className="text-xs text-slate-500">C-PACE enables <span className="font-semibold text-slate-700">processing</span>.</p>
        <p className="text-xs text-[#1e3a5f]">R-PACE enables <span className="font-semibold">execution</span>.</p>
      </div>
    </div>
  );
}


/* ─── JOURNEY TIMELINE ─── */
function JourneyTimeline() {
  const steps = [
    { num: '01', title: 'Client Case Comes to You', desc: 'Inactive company, pending filings, uncertainty.', accent: 'bg-slate-100 text-slate-600' },
    { num: '02', title: 'Use R-PACE', desc: 'Quick case check and structured assessment.', accent: 'bg-blue-50 text-[#1e3a5f]' },
    { num: '03', title: 'Execution Support', desc: 'Financials (CA), filings (CS), coordination handled.', accent: 'bg-amber-50 text-amber-700' },
    { num: '04', title: 'C-PACE Processing', desc: 'Centralized government processing.', accent: 'bg-purple-50 text-purple-700' },
    { num: '05', title: 'Clean Exit Delivered', desc: 'Client closure achieved, credibility strengthened.', accent: 'bg-green-50 text-green-700' },
  ];

  return (
    <div className="relative">
      {/* Desktop: horizontal */}
      <div className="hidden md:block">
        <div className="absolute top-1/2 left-[5%] right-[5%] h-px bg-slate-200 -translate-y-1/2" />
        <div className="flex items-center justify-between relative">
          {steps.map((step, i) => (
            <div key={i} className={`flex flex-col items-center relative ${i % 2 === 0 ? 'mt-0' : 'mt-24'}`} style={{ flex: 1 }} data-testid={`journey-step-${i}`}>
              <div className={`w-10 h-10 rounded-full ${step.accent} flex items-center justify-center text-xs font-bold z-10 border-2 border-white shadow-sm`}>
                {step.num}
              </div>
              <div className="mt-3 text-center max-w-[150px]">
                <p className="text-sm font-semibold text-slate-800 leading-tight">{step.title}</p>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: vertical */}
      <div className="md:hidden space-y-0 relative pl-8">
        <div className="absolute left-[15px] top-4 bottom-4 w-px bg-slate-200" />
        {steps.map((step, i) => (
          <div key={i} className="relative pb-6 last:pb-0" data-testid={`journey-step-mobile-${i}`}>
            <div className={`absolute left-[-23px] top-0 w-8 h-8 rounded-full ${step.accent} flex items-center justify-center text-[10px] font-bold z-10 border-2 border-white shadow-sm`}>
              {step.num}
            </div>
            <div className="pl-3">
              <p className="text-sm font-semibold text-slate-800">{step.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ─── REUSABLE COMPONENTS ─── */
function ValueCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="w-10 h-10 rounded-lg bg-[#1e3a5f]/5 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[#1e3a5f]" />
      </div>
      <h3 className="font-semibold text-slate-900 mb-2 text-sm">{title}</h3>
      <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

function ScreenshotCard({ src, step, label, testId }) {
  return (
    <div className="text-center" data-testid={testId}>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow mb-4">
        <img
          src={src}
          alt={label}
          className="w-full h-auto"
          loading="lazy"
        />
      </div>
      <p className="text-[#1e3a5f] font-semibold text-sm">{step}</p>
      <p className="text-slate-600 text-sm mt-0.5">{label}</p>
    </div>
  );
}
